import { registerSW } from 'virtual:pwa-register';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';

import App from './App.tsx';
import { ReactQueryDevtools } from '@/app/debug/ReactQueryDevtools';
import { initSentry } from '@/app/monitoring/initSentry';
import { AppErrorBoundary } from '@/shared/ui/app-error-boundary/AppErrorBoundary.tsx';
import { Notifications } from '@/components/notification/Notifications.tsx';
import i18n from '../src/i18n';
import customTheme from './theme-t4e.ts';

import './index.css';
import './fonts.css';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

const origUrl = location.href;
const updateSW = registerSW({
  immediate: false,
  // this can be removed when reload is fixed in flutter
  onNeedRefresh: async function () {
    // this causes a short white screen
    document.getElementById('root')!.textContent = '';
    history.replaceState({}, '', origUrl);
    await updateSW(true);
  },
});

// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

initSentry();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _query) => {
      // console.debug(error, query);
    },
  }),
  mutationCache: new MutationCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _variables, _context, _mutation) => {
      // console.debug(error, variables, context, mutation);
    },
  }),
});

ReactDOM.createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={customTheme}>
      <CssBaseline enableColorScheme />
      <I18nextProvider i18n={i18n}>
        <AppErrorBoundary boundaryId="application" onReset={() => window.location.reload()}>
          <Notifications />
          <App />
        </AppErrorBoundary>
      </I18nextProvider>
      {import.meta.env.DEV && <ReactQueryDevtools />}
    </ThemeProvider>
  </QueryClientProvider>
  // </React.StrictMode>
);
